import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';

import { ThemeProvider, theme } from '@octane/spark';
import {
  Title,
  Payment,
  PaymentTitle,
  PaymentAmount,
  Container,
} from './estimator-card.styles';
import CreditSlots from '../ui/credit-slots/credit-slots';
import SliderAmount from '../ui/slider-amount/slider-amount';
import TermLength from '../ui/term-length/term-length';
import useEstimatorStore from '../ui/estimator-store/estimator-store';
import useCountUp from '../payment-calculator-hooks/count-up-hooks';

const EstimatorCard = ({
  creditScoreSlots,
  downPayment,
  salesPrice,
  terms,
  preselectedTerm,
  preselectedCreditScoreSlot,
  themeStore,
}) => {
  const { monthlyPayment, updateParam } = useEstimatorStore();

  const [salesPriceValue, setSalesPriceValue] = useState(salesPrice.price);
  const [downPaymentValue, setDownPaymentValue] = useState(downPayment);

  const [prevFrom, setPrevFrom] = useState(0);
  const [prevTo, setPrevTo] = useState(0);

   const maxDownPaymentLimit = useMemo(() => {
      return Math.max(salesPriceValue - 1000, 0);
   }, [salesPriceValue]);

  const numberAnimationConfig = {
    isCounting: true,
    duration: 0.5,
    easing: 'easeOutCubic',
  };

   const savePrevFrom = Math.max(prevFrom, 100);
   const saveMonthlyPaymentFrom = Math.max(monthlyPayment.from, 100);

   const { value: fromValue, reset: animateFromValue } = useCountUp({
      ...numberAnimationConfig,
      start: savePrevFrom,
      end: saveMonthlyPaymentFrom,
      onComplete: () => {
         setPrevFrom(saveMonthlyPaymentFrom);
      },
   });

  const { value: toValue, reset: animateToValue } = useCountUp({
    ...numberAnimationConfig,
    start: prevTo,
    end: monthlyPayment.to,
    onComplete: () => {
      setPrevTo(monthlyPayment.to);
    },
  });

  useEffect(() => {
    animateFromValue();
    animateToValue();
  }, [animateFromValue, animateToValue, monthlyPayment]);

  useEffect(() => {
   const maxDown = Math.max(salesPriceValue - 1000, 0);
      if (downPaymentValue > maxDown) {
         setDownPaymentValue(maxDown);
         updateParam('cashDown', maxDown);
      }
   }, [salesPriceValue]);

  const handleSalesPriceChange = (price) => {
    setSalesPriceValue(price);
    updateParam('price', price);

    const maxDown = Math.max(price - 1000, 0);
    if (downPaymentValue > maxDown) {
       handleCashDownChange(maxDown);
    }
  };

  const handleCreditScoreSelect = (aprLowerBound, aprUpperBound, label) => {
    updateParam('aprLowerBound', aprLowerBound);
    updateParam('aprUpperBound', aprUpperBound);
    updateParam('label', label);
  };

  const handleTermSelect = (term) => {
    updateParam('term', term);
  };

  const handleCashDownChange = (value) => {
    const maxDown = Math.max(salesPriceValue - 1000, 0);
    const adjustedValue = Math.min(value, maxDown);
    setDownPaymentValue(adjustedValue);
    updateParam('cashDown', adjustedValue);
  };
  const brandConfig = theme
  if (themeStore?.theme?.color) {
    brandConfig.colors.brand.rideOctaneBlue = themeStore.theme.color;
  }
  return (
    <Container>
      <ThemeProvider theme={brandConfig}>
      <Title>Estimate your payment</Title>
      <Payment>
        <PaymentTitle>Est. Monthly Payment Range*</PaymentTitle>
        <PaymentAmount>${fromValue} - ${toValue}</PaymentAmount>
      </Payment>
      <SliderAmount
        data-testid="vehicle-price-input"
        label="Vehicle Sales Price"
        min={salesPrice.min}
        max={salesPrice.max}
        value={salesPriceValue}
        step={100}
        onChange={handleSalesPriceChange}
        fill={themeStore?.theme?.color}
      />
      <CreditSlots
        data-testid="credit-slots"
        slots={creditScoreSlots}
        preselectedCreditScoreSlot={preselectedCreditScoreSlot}
        onSlotSelect={handleCreditScoreSelect}
      />
      <TermLength
        data-testid="term-length"
        terms={terms}
        onTermSelect={handleTermSelect}
        preselectedTerm={preselectedTerm}
      />
      <SliderAmount
        data-testid="down-payment-slider"
        label="Cash Downpayment"
        min={0}
        max={maxDownPaymentLimit}
        value={downPaymentValue}
        step={100}
        onChange={handleCashDownChange}
        fill={themeStore?.theme?.color}
      />
      </ThemeProvider>
    </Container>
  );
};

EstimatorCard.propTypes = {
   themeStore: PropTypes.object,
  };

export default EstimatorCard;
